import React from "react";
import { Search, GitMerge, CheckCircle, X, RotateCcw } from "react-feather";
import Select from "react-select";

const SELECT_MENU_ZINDEX = 2147483647;

const getSelectPortalTarget = () =>
  typeof document !== "undefined" ? document.body : null;

const selectStyles = {
  control: (base, state) => ({
    ...base,
    width: "100%",
    minHeight: 42,
    borderRadius: 14,
    borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.12)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(37, 99, 235, 0.12)" : "none",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    "&:hover": {
      borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.22)",
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: "#94a3b8",
    fontWeight: 700,
  }),
  singleValue: (base) => ({
    ...base,
    color: "#0f172a",
    fontWeight: 850,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: SELECT_MENU_ZINDEX,
  }),
  menu: (base) => ({
    ...base,
    zIndex: SELECT_MENU_ZINDEX,
    borderRadius: 14,
    overflow: "hidden",
    border: "1px solid rgba(15, 23, 42, 0.10)",
    boxShadow: "0 18px 42px rgba(15, 23, 42, 0.18)",
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 260,
    padding: 6,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    fontWeight: state.isSelected ? 850 : 700,
    cursor: "pointer",
  }),
};

const TableFilterBar = ({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  zoneFilter,
  setZoneFilter,
  serverFilter,
  setServerFilter,
  statusOptions,
  zoneOptions,
  serverOptions,
  mergeMode,
  setMergeMode,
  selectedForMerge,
  handleMerge,
  setSelectedForMerge,
}) => {
  const hasFilters = Boolean(searchTerm || statusFilter || zoneFilter || serverFilter);

  const clearFilters = () => {
    setSearchTerm?.("");
    setStatusFilter?.(null);
    setZoneFilter?.(null);
    setServerFilter?.(null);
  };

  const toggleMergeMode = () => {
    setMergeMode?.(!mergeMode);
    setSelectedForMerge?.([]);
  };

  return (
    <div className="ns-table-filter-bar">
      <style>{filterStyles}</style>

      <div className="ns-table-filter-search">
        <Search size={17} />
        <input
          type="text"
          placeholder="Search table or zone..."
          value={searchTerm || ""}
          onChange={(event) => setSearchTerm?.(event.target.value)}
          aria-label="Search table or zone"
        />
        {searchTerm ? (
          <button type="button" onClick={() => setSearchTerm?.("")} aria-label="Clear search">
            <X size={14} />
          </button>
        ) : null}
      </div>

      <div className="ns-table-filter-select">
        <Select
          options={statusOptions}
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="Status"
          isClearable
          classNamePrefix="ns-table-rs"
          styles={selectStyles}
          menuPortalTarget={getSelectPortalTarget()}
          menuPosition="fixed"
          menuPlacement="auto"
          menuShouldScrollIntoView={false}
          maxMenuHeight={260}
        />
      </div>

      <div className="ns-table-filter-select">
        <Select
          options={zoneOptions}
          value={zoneFilter}
          onChange={setZoneFilter}
          placeholder="Zone"
          isClearable
          classNamePrefix="ns-table-rs"
          styles={selectStyles}
          menuPortalTarget={getSelectPortalTarget()}
          menuPosition="fixed"
          menuPlacement="auto"
          menuShouldScrollIntoView={false}
          maxMenuHeight={260}
        />
      </div>

      <div className="ns-table-filter-select">
        <Select
          options={serverOptions}
          value={serverFilter}
          onChange={setServerFilter}
          placeholder="Server"
          isClearable
          classNamePrefix="ns-table-rs"
          styles={selectStyles}
          menuPortalTarget={getSelectPortalTarget()}
          menuPosition="fixed"
          menuPlacement="auto"
          menuShouldScrollIntoView={false}
          maxMenuHeight={260}
        />
      </div>

      {hasFilters ? (
        <button type="button" className="ns-table-filter-btn soft" onClick={clearFilters}>
          <RotateCcw size={15} />
          Reset
        </button>
      ) : null}

      <button
        type="button"
        className={`ns-table-filter-btn ${mergeMode ? "danger" : "primary"}`}
        onClick={toggleMergeMode}
      >
        {mergeMode ? <X size={15} /> : <GitMerge size={15} />}
        {mergeMode ? "Cancel Merge" : "Merge Tables"}
      </button>

      {mergeMode && selectedForMerge.length >= 2 ? (
        <button type="button" className="ns-table-filter-btn success" onClick={handleMerge}>
          <CheckCircle size={15} />
          Confirm Merge ({selectedForMerge.length})
        </button>
      ) : null}

      {mergeMode ? (
        <span className="ns-table-merge-hint">
          Select available or dirty tables · {selectedForMerge.length} selected
        </span>
      ) : null}
    </div>
  );
};

const filterStyles = `
  .ns-table-filter-bar {
    position: relative;
    z-index: 30;
    width: 100%;
    display: grid;
    grid-template-columns: minmax(220px, 1.5fr) minmax(145px, 0.75fr) minmax(145px, 0.75fr) minmax(145px, 0.75fr) auto auto auto;
    gap: 10px;
    align-items: center;
  }

  .ns-table-filter-search {
    position: relative;
    min-width: 0;
  }

  .ns-table-filter-search > svg {
    position: absolute;
    left: 13px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    pointer-events: none;
  }

  .ns-table-filter-search input {
    width: 100%;
    height: 42px;
    border-radius: 14px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #ffffff;
    padding: 0 42px 0 40px;
    color: #0f172a;
    font-size: 0.84rem;
    font-weight: 780;
    outline: none;
    box-shadow: 0 6px 14px rgba(15, 23, 42, 0.04);
    transition: border-color 0.16s ease, box-shadow 0.16s ease;
  }

  .ns-table-filter-search input:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
  }

  .ns-table-filter-search input::placeholder {
    color: #94a3b8;
    font-weight: 700;
  }

  .ns-table-filter-search button {
    position: absolute;
    right: 7px;
    top: 50%;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
    border-radius: 10px;
    border: 0;
    background: #f1f5f9;
    color: #64748b;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .ns-table-filter-select {
    min-width: 0;
    position: relative;
    z-index: 31;
  }

  .ns-table-filter-btn {
    min-height: 42px;
    border-radius: 14px;
    border: 0;
    padding: 0 13px;
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    font-size: 0.78rem;
    font-weight: 950;
    cursor: pointer;
    white-space: nowrap;
    box-shadow: 0 10px 22px rgba(15, 23, 42, 0.10);
    transition: transform 0.15s ease, box-shadow 0.15s ease;
  }

  .ns-table-filter-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 14px 28px rgba(15, 23, 42, 0.14);
  }

  .ns-table-filter-btn.primary {
    background: #1d4ed8;
  }

  .ns-table-filter-btn.success {
    background: #16a34a;
  }

  .ns-table-filter-btn.danger {
    background: #dc2626;
  }

  .ns-table-filter-btn.soft {
    background: #f8fafc;
    color: #334155;
    border: 1px solid rgba(15, 23, 42, 0.08);
  }

  .ns-table-merge-hint {
    grid-column: 1 / -1;
    color: #92400e;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 999px;
    padding: 7px 11px;
    font-size: 0.76rem;
    font-weight: 850;
    width: fit-content;
  }

  @media (max-width: 1180px) {
    .ns-table-filter-bar {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 720px) {
    .ns-table-filter-bar {
      grid-template-columns: 1fr;
    }

    .ns-table-filter-btn {
      width: 100%;
    }
  }
`;

export default TableFilterBar;
